import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  PlayIcon,
  EyeIcon,
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

import { useContentValidation } from '../../store/contentAdminStore';
import type { ValidationError } from '../../services/contentAdminService';

interface ValidationPanelProps {
  versionId: string;
  content: any;
  onChange: (content: any) => void;
  validationErrors: ValidationError[];
}

type ValidationSeverity = 'error' | 'warning' | 'info';

interface ValidationSection {
  id: string;
  name: string;
  description: string;
  checks: ValidationCheck[];
}

interface ValidationCheck {
  id: string;
  name: string;
  description: string;
  severity: ValidationSeverity;
  status: 'pending' | 'running' | 'passed' | 'failed';
  message?: string;
  details?: string;
  field?: string;
}

const validationSections: ValidationSection[] = [
  {
    id: 'questions',
    name: 'Questões',
    description: 'Validação do questionário e dimensões',
    checks: [
      {
        id: 'questions_exist',
        name: 'Questões configuradas',
        description: 'Verifica se existem questões configuradas',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'dimensions_exist',
        name: 'Dimensões configuradas',
        description: 'Verifica se existem dimensões configuradas',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'questions_per_dimension',
        name: 'Distribuição por dimensão',
        description: 'Verifica se todas as dimensões têm questões',
        severity: 'warning',
        status: 'pending',
      },
      {
        id: 'question_text_quality',
        name: 'Qualidade do texto',
        description: 'Verifica se os textos das questões são adequados',
        severity: 'info',
        status: 'pending',
      },
    ],
  },
  {
    id: 'matrix',
    name: 'Matriz de Avaliação',
    description: 'Validação da matriz de cálculo de perfis',
    checks: [
      {
        id: 'profiles_exist',
        name: 'Perfis configurados',
        description: 'Verifica se existem perfis na matriz',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'matrix_dimensions_sync',
        name: 'Sincronização de dimensões',
        description: 'Verifica se as dimensões da matriz estão sincronizadas',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'profile_weights',
        name: 'Pesos dos perfis',
        description: 'Verifica se todos os perfis têm pesos válidos',
        severity: 'warning',
        status: 'pending',
      },
      {
        id: 'calculation_config',
        name: 'Configuração de cálculo',
        description: 'Verifica se a configuração de cálculo está correta',
        severity: 'info',
        status: 'pending',
      },
    ],
  },
  {
    id: 'profiles',
    name: 'Perfis de Relatório',
    description: 'Validação do conteúdo dos relatórios',
    checks: [
      {
        id: 'report_profiles_exist',
        name: 'Perfis de relatório existem',
        description: 'Verifica se existem perfis configurados para relatórios',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'profile_content_complete',
        name: 'Conteúdo completo',
        description: 'Verifica se todos os perfis têm conteúdo completo',
        severity: 'warning',
        status: 'pending',
      },
      {
        id: 'action_plans_exist',
        name: 'Planos de ação',
        description: 'Verifica se todos os perfis têm planos de ação',
        severity: 'warning',
        status: 'pending',
      },
      {
        id: 'strategies_exist',
        name: 'Estratégias específicas',
        description: 'Verifica se todos os perfis têm estratégias específicas',
        severity: 'info',
        status: 'pending',
      },
    ],
  },
  {
    id: 'spider',
    name: 'Gráfico Spider',
    description: 'Validação da configuração do gráfico',
    checks: [
      {
        id: 'spider_axes_exist',
        name: 'Eixos configurados',
        description: 'Verifica se existem eixos configurados no gráfico',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'spider_axes_linked',
        name: 'Eixos vinculados',
        description: 'Verifica se os eixos estão vinculados às dimensões',
        severity: 'warning',
        status: 'pending',
      },
      {
        id: 'spider_visual_config',
        name: 'Configuração visual',
        description: 'Verifica se a configuração visual está adequada',
        severity: 'info',
        status: 'pending',
      },
    ],
  },
  {
    id: 'integration',
    name: 'Integração',
    description: 'Validação da integração entre componentes',
    checks: [
      {
        id: 'profile_matrix_consistency',
        name: 'Consistência perfis-matriz',
        description: 'Verifica se os perfis da matriz e dos relatórios são consistentes',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'dimension_consistency',
        name: 'Consistência de dimensões',
        description: 'Verifica se as dimensões são consistentes entre componentes',
        severity: 'error',
        status: 'pending',
      },
      {
        id: 'spider_dimension_consistency',
        name: 'Dimensões spider-questões',
        description: 'Verifica se os eixos do spider correspondem às dimensões',
        severity: 'warning',
        status: 'pending',
      },
    ],
  },
];

export const ValidationPanel: React.FC<ValidationPanelProps> = ({
  versionId,
  content,
  onChange,
  validationErrors,
}) => {
  const [localChecks, setLocalChecks] = useState<ValidationCheck[]>([]);
  const [isRunningValidation, setIsRunningValidation] = useState(false);
  const [lastValidation, setLastValidation] = useState<Date | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['questions']));

  const { validateContent, previewContent, isValidating } = useContentValidation();

  // Initialize checks
  useEffect(() => {
    const allChecks = validationSections.flatMap(section => 
      section.checks.map(check => ({
        ...check,
        field: `${section.id}.${check.id}`,
      }))
    );
    setLocalChecks(allChecks);
  }, []);

  // Update checks based on validation errors
  useEffect(() => {
    setLocalChecks(prevChecks => 
      prevChecks.map(check => {
        const relatedError = validationErrors.find(error => 
          error.field.includes(check.field || check.id)
        );
        
        if (relatedError) {
          return {
            ...check,
            status: 'failed' as const,
            message: relatedError.message,
            details: relatedError.details,
          };
        }
        
        return check;
      })
    );
  }, [validationErrors]);

  const runLocalValidation = async () => {
    setIsRunningValidation(true);
    
    // Reset all checks
    setLocalChecks(prevChecks => 
      prevChecks.map(check => ({ ...check, status: 'running' as const, message: undefined }))
    );

    // Simulate validation checks with delays
    const checkPromises = validationSections.map(async (section, sectionIndex) => {
      await new Promise(resolve => setTimeout(resolve, sectionIndex * 200));
      
      return section.checks.map(async (check, checkIndex) => {
        await new Promise(resolve => setTimeout(resolve, checkIndex * 100));
        
        const result = await performCheck(section.id, check.id, content);
        
        setLocalChecks(prevChecks => 
          prevChecks.map(c => 
            c.id === check.id && c.field?.startsWith(section.id) 
              ? { ...c, ...result } 
              : c
          )
        );
        
        return result;
      });
    });

    await Promise.all(checkPromises.flat());
    setIsRunningValidation(false);
    setLastValidation(new Date());
  };

  const performCheck = async (sectionId: string, checkId: string, content: any): Promise<Partial<ValidationCheck>> => {
    switch (`${sectionId}.${checkId}`) {
      case 'questions.questions_exist':
        const questionsExist = content.questions?.questoes?.length > 0;
        return {
          status: questionsExist ? 'passed' : 'failed',
          message: questionsExist ? 'Questões configuradas' : 'Nenhuma questão encontrada',
        };

      case 'questions.dimensions_exist':
        const dimensionsExist = content.questions?.dimensoes?.length > 0;
        return {
          status: dimensionsExist ? 'passed' : 'failed',
          message: dimensionsExist ? 'Dimensões configuradas' : 'Nenhuma dimensão encontrada',
        };

      case 'questions.questions_per_dimension':
        const dimensions = content.questions?.dimensoes || [];
        const questions = content.questions?.questoes || [];
        const dimensionsWithQuestions = dimensions.filter(dim => 
          questions.some(q => q.dimensao === dim.id)
        );
        const hasAllDimensions = dimensionsWithQuestions.length === dimensions.length;
        return {
          status: hasAllDimensions ? 'passed' : 'failed',
          message: hasAllDimensions 
            ? 'Todas as dimensões têm questões' 
            : `${dimensionsWithQuestions.length}/${dimensions.length} dimensões têm questões`,
        };

      case 'matrix.profiles_exist':
        const profilesExist = Object.keys(content.evaluation_matrix?.perfis || {}).length > 0;
        return {
          status: profilesExist ? 'passed' : 'failed',
          message: profilesExist ? 'Perfis configurados na matriz' : 'Nenhum perfil encontrado na matriz',
        };

      case 'matrix.matrix_dimensions_sync':
        const matrixDimensions = content.evaluation_matrix?.dimensoes || [];
        const questionDimensions = content.questions?.dimensoes || [];
        const synced = matrixDimensions.length === questionDimensions.length &&
                      matrixDimensions.every(md => questionDimensions.find(qd => qd.id === md.id));
        return {
          status: synced ? 'passed' : 'failed',
          message: synced ? 'Dimensões sincronizadas' : 'Dimensões desatualizadas',
        };

      case 'profiles.report_profiles_exist':
        const reportProfilesExist = Object.keys(content.report_contents?.perfis || {}).length > 0;
        return {
          status: reportProfilesExist ? 'passed' : 'failed',
          message: reportProfilesExist ? 'Perfis de relatório configurados' : 'Nenhum perfil de relatório encontrado',
        };

      case 'spider.spider_axes_exist':
        const axesExist = content.spider_chart?.eixos?.length > 0;
        return {
          status: axesExist ? 'passed' : 'failed',
          message: axesExist ? 'Eixos configurados no gráfico' : 'Nenhum eixo encontrado no gráfico',
        };

      case 'integration.profile_matrix_consistency':
        const matrixProfiles = Object.keys(content.evaluation_matrix?.perfis || {});
        const reportProfiles = Object.keys(content.report_contents?.perfis || {});
        const consistent = matrixProfiles.length === reportProfiles.length &&
                          matrixProfiles.every(mp => reportProfiles.includes(mp));
        return {
          status: consistent ? 'passed' : 'failed',
          message: consistent ? 'Perfis consistentes' : 'Perfis inconsistentes entre matriz e relatórios',
        };

      default:
        return {
          status: 'passed',
          message: 'Verificação simulada',
        };
    }
  };

  const runServerValidation = async () => {
    await validateContent(versionId);
    setLastValidation(new Date());
  };

  const handleRunValidation = async () => {
    await Promise.all([
      runLocalValidation(),
      runServerValidation(),
    ]);
  };

  const handlePreview = async () => {
    await previewContent(versionId);
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const getSectionStatus = (sectionId: string) => {
    const sectionChecks = localChecks.filter(check => check.field?.startsWith(sectionId));
    const failed = sectionChecks.filter(check => check.status === 'failed');
    const running = sectionChecks.filter(check => check.status === 'running');
    
    if (running.length > 0) return 'running';
    if (failed.some(check => check.severity === 'error')) return 'error';
    if (failed.some(check => check.severity === 'warning')) return 'warning';
    return 'passed';
  };

  const getStatusIcon = (status: string, severity?: ValidationSeverity) => {
    switch (status) {
      case 'running':
        return <ArrowPathIcon className="w-4 h-4 animate-spin text-blue-500" />;
      case 'passed':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'failed':
        switch (severity) {
          case 'error':
            return <XCircleIcon className="w-4 h-4 text-red-500" />;
          case 'warning':
            return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />;
          default:
            return <InformationCircleIcon className="w-4 h-4 text-blue-500" />;
        }
      default:
        return <div className="w-4 h-4 rounded-full bg-gray-300" />;
    }
  };

  const getSummary = () => {
    const totalChecks = localChecks.length;
    const passedChecks = localChecks.filter(check => check.status === 'passed').length;
    const failedChecks = localChecks.filter(check => check.status === 'failed').length;
    const runningChecks = localChecks.filter(check => check.status === 'running').length;
    
    return { totalChecks, passedChecks, failedChecks, runningChecks };
  };

  const { totalChecks, passedChecks, failedChecks, runningChecks } = getSummary();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Validação de Conteúdo
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Verifique a integridade e qualidade do conteúdo
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handlePreview}
              disabled={isValidating}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
              Preview
            </button>
            
            <button
              onClick={handleRunValidation}
              disabled={isRunningValidation || isValidating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isRunningValidation || isValidating ? (
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
              ) : (
                <PlayIcon className="w-4 h-4" />
              )}
              {isRunningValidation || isValidating ? 'Validando...' : 'Executar Validação'}
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{totalChecks}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{passedChecks}</div>
            <div className="text-sm text-gray-600">Aprovados</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{failedChecks}</div>
            <div className="text-sm text-gray-600">Falhas</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{runningChecks}</div>
            <div className="text-sm text-gray-600">Executando</div>
          </div>
        </div>

        {lastValidation && (
          <div className="mt-4 text-sm text-gray-600">
            Última validação: {lastValidation.toLocaleString()}
          </div>
        )}
      </div>

      {/* Validation Sections */}
      <div className="space-y-4">
        {validationSections.map((section) => {
          const sectionStatus = getSectionStatus(section.id);
          const isExpanded = expandedSections.has(section.id);
          const sectionChecks = localChecks.filter(check => check.field?.startsWith(section.id));

          return (
            <div key={section.id} className="bg-white rounded-lg shadow">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(sectionStatus)}
                  <div>
                    <h4 className="font-medium text-gray-900">{section.name}</h4>
                    <p className="text-sm text-gray-600">{section.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="text-sm text-gray-500">
                    {sectionChecks.filter(c => c.status === 'passed').length}/{sectionChecks.length}
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-gray-200"
                  >
                    <div className="p-6 space-y-3">
                      {sectionChecks.map((check) => (
                        <div key={check.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200">
                          {getStatusIcon(check.status, check.severity)}
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h5 className="font-medium text-gray-900">{check.name}</h5>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                check.severity === 'error' 
                                  ? 'bg-red-100 text-red-700'
                                  : check.severity === 'warning'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {check.severity}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 mt-1">{check.description}</p>
                            
                            {check.message && (
                              <p className={`text-sm mt-2 ${
                                check.status === 'failed' 
                                  ? 'text-red-600' 
                                  : check.status === 'passed'
                                  ? 'text-green-600'
                                  : 'text-gray-600'
                              }`}>
                                {check.message}
                              </p>
                            )}
                            
                            {check.details && (
                              <p className="text-xs text-gray-500 mt-1">{check.details}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* External Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ClipboardDocumentCheckIcon className="w-5 h-5" />
            Erros de Validação do Servidor
          </h4>
          
          <div className="space-y-3">
            {validationErrors.map((error, index) => (
              <div key={index} className={`p-3 rounded-lg border-l-4 ${
                error.severity === 'error'
                  ? 'bg-red-50 border-red-400'
                  : error.severity === 'warning'
                  ? 'bg-yellow-50 border-yellow-400'
                  : 'bg-blue-50 border-blue-400'
              }`}>
                <div className="flex items-start gap-3">
                  {getStatusIcon('failed', error.severity)}
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{error.field}</h5>
                    <p className="text-sm text-gray-600 mt-1">{error.message}</p>
                    {error.details && (
                      <p className="text-xs text-gray-500 mt-2">{error.details}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};